
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useClubs } from '@/hooks/useClubs';
import { useCoachStats } from '@/hooks/useCoachStats';
import { Users, UserCheck, UserX, Building2, Target, TrendingUp } from 'lucide-react';

export const CoachStatistics = () => {
  const [selectedClubId, setSelectedClubId] = useState<string>('all');
  const { clubs, loading: clubsLoading } = useClubs();
  const { stats, loading: statsLoading } = useCoachStats(selectedClubId);

  const handleClubChange = (value: string) => {
    setSelectedClubId(value);
  };

  return (
    <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              Coach Statistics
            </CardTitle>
            <CardDescription className="text-gray-600">
              Monitor coaching staff performance and coverage
            </CardDescription>
          </div>
          <div className="w-64">
            <Select value={selectedClubId} onValueChange={handleClubChange} disabled={clubsLoading}>
              <SelectTrigger>
                <SelectValue placeholder="Select club..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clubs</SelectItem>
                {clubs.map((club) => (
                  <SelectItem key={club.id} value={club.id}>
                    {club.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {statsLoading ? (
          <div className="grid gap-6 md:grid-cols-4 mb-8">
            {[...Array(4)].map((_, i) => (
              <Card key={i} className="animate-pulse bg-gray-100">
                <CardContent className="p-6">
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded mb-1"></div>
                  <div className="h-3 bg-gray-200 rounded"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <>
            {/* Coach Overview Cards */}
            <div className="grid gap-6 md:grid-cols-4 mb-8">
              <Card className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 bg-gradient-to-br from-white to-blue-50 border-0 shadow-md">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Coaches</CardTitle>
                  <Users className="w-4 h-4 text-blue-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900 mb-1">{stats.totalCoaches}</div>
                  <div className="flex items-center text-sm text-blue-600">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    <span>In selected scope</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 bg-gradient-to-br from-white to-green-50 border-0 shadow-md">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Active Coaches</CardTitle>
                  <UserCheck className="w-4 h-4 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900 mb-1">{stats.coachesWithTeams}</div>
                  <div className="flex items-center text-sm text-green-600">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    <span>With assigned teams</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 bg-gradient-to-br from-white to-orange-50 border-0 shadow-md">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Unassigned</CardTitle>
                  <UserX className="w-4 h-4 text-orange-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900 mb-1">{stats.coachesWithoutTeams}</div>
                  <div className="flex items-center text-sm text-orange-600">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    <span>Available coaches</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 bg-gradient-to-br from-white to-purple-50 border-0 shadow-md">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Team Coverage</CardTitle>
                  <Target className="w-4 h-4 text-purple-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900 mb-1">{stats.teamCoverage}%</div>
                  <div className="flex items-center text-sm text-purple-600">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    <span>Teams with coaches</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Coach Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Coach Details</h3>
              {stats.coaches.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No coaches found {selectedClubId !== 'all' ? 'for the selected club' : ''}
                </div>
              ) : (
                <div className="grid gap-4">
                  {stats.coaches.map((coach) => (
                    <Card key={coach.id} className="border border-gray-200 hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">
                              {coach.first_name} {coach.last_name}
                            </h4>
                            <p className="text-sm text-gray-600 mb-2">{coach.email}</p>
                            {coach.teams.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {coach.teams.map((team) => (
                                  <Badge key={team.id} variant="secondary" className="text-xs">
                                    <Building2 className="w-3 h-3 mr-1" />
                                    {team.name} ({team.club_name})
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <Badge variant="outline" className="text-xs text-orange-600">
                                No teams assigned
                              </Badge>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-gray-500">
                              {coach.teams.length} team{coach.teams.length !== 1 ? 's' : ''}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
